import { z } from 'zod';
import {
  NORMALIZED_EVENT_CATEGORIES,
  NORMALIZED_EVENT_STATUSES,
  PLATFORM_EVENT_CATEGORIES,
  type NormalizedEvent as NormalizedEventType,
} from '@loci/types';

/**
 * Runtime validation for the normalized event DTO (PIP-8).
 *
 * Every `VenueIngestor.normalize()` implementation (PIP-2) must return values
 * that pass this schema. Downstream consumers can treat `z.infer<typeof
 * normalizedEventSchema>` as structurally identical to `NormalizedEvent` from
 * `@loci/types` — the shared `NORMALIZED_EVENT_CATEGORIES` /
 * `PLATFORM_EVENT_CATEGORIES` / `NORMALIZED_EVENT_STATUSES` tuples keep the
 * two definitions in lock-step.
 *
 * Validation rules:
 *   - `minPrice`, `maxPrice` are non-negative
 *   - `startDatetime` < `endDatetime` when both are present (equality allowed
 *     for instantaneous events)
 *   - `minPrice` ≤ `maxPrice` when both are present
 *   - All URL fields (including `scrapeSource`) parse as real URLs
 *   - `timezone` looks like an IANA identifier AND resolves via `Intl`
 *   - `sourceHtmlHash` is 64 hex chars (SHA-256)
 */

export const normalizedEventCategorySchema = z.enum(NORMALIZED_EVENT_CATEGORIES);
export const platformEventCategorySchema = z.enum(PLATFORM_EVENT_CATEGORIES);
export const normalizedEventStatusSchema = z.enum(NORMALIZED_EVENT_STATUSES);

// Zod ≥3.20 validates ISO-8601 with offset when {offset: true} is passed.
const isoDatetime = z.string().datetime({ offset: true, message: 'Must be ISO-8601 with offset' });

// Two-layer tz validation:
//   1. Regex shape check — rejects abbreviations like `EST` / `PST` that are
//      ambiguous w.r.t. DST even though Intl happens to accept them.
//   2. `Intl.DateTimeFormat` resolve check — rejects structurally-plausible
//      but non-existent zones like `America/Atlantis` that the regex lets
//      through.
const ianaTimezone = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z][A-Za-z0-9_+-]*){1,2}$/, 'Must be an IANA timezone')
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Must be an IANA timezone that resolves via Intl' },
  );

const sha256Hex = z.string().regex(/^[a-f0-9]{64}$/, 'Must be 64-char lowercase hex');

export const normalizedEventSchema = z
  .object({
    eventId: z.string().uuid('eventId must be a UUID'),
    sourceEventId: z.string().min(1),
    venueId: z.string().min(1),
    venueName: z.string().min(1),
    // PIP-164 — optional Google Places id resolved by the source or an
    // ingest-time lookup (e.g. the venue-site lane's enriched Location rows).
    // Absent from this schema until PIP-168, which silently STRIPPED the
    // field in `validateStampedEvents` (Zod objects drop unknown keys).
    googlePlaceId: z.string().min(1).optional(),
    title: z.string().min(1).max(500),
    rawCategory: normalizedEventCategorySchema,
    platformCategory: platformEventCategorySchema,
    startDatetime: isoDatetime,
    // PIP-72 — honest-signal flag. `true` when the source explicitly surfaced
    // a wall-clock start time; `false`/`undefined` when the source omitted it
    // and the transformer defaulted (e.g. iCal `DATE`, fake-midnight ISO).
    // Persisters write the column as `false` when omitted so the discover
    // surface can render "TBA" instead of a confidently wrong time.
    startTimeKnown: z.boolean().optional(),
    timezone: ianaTimezone,
    status: normalizedEventStatusSchema,
    detailUrl: z.string().url(),
    sourceHtmlHash: sha256Hex,
    firstSeenAt: isoDatetime,
    lastSeenAt: isoDatetime,
    lastChangedAt: isoDatetime,
    scrapeSource: z.string().url(),
    scrapeUserAgent: z.string().min(1),

    headliner: z.string().min(1).optional(),
    supporting: z.array(z.string().min(1)).optional(),
    doorsDatetime: isoDatetime.optional(),
    endDatetime: isoDatetime.optional(),
    ticketUrl: z.string().url().optional(),
    imageUrl: z.string().url().optional(),
    // PIP-235 — size-banded renditions of the same cover image. Declared here
    // as well as on the TS interface because Zod's default key-stripping would
    // otherwise delete it from every framework-validated event before the
    // persister ran — the exact defect PIP-168/PIP-169 hit with `isFree` /
    // `rsvpOnly`. Each entry is a real published URL (`.url()` rejects the
    // empty string), and a band the source did not publish stays absent.
    imageUrls: z
      .object({
        thumbnail: z.string().url().optional(),
        medium: z.string().url().optional(),
        large: z.string().url().optional(),
      })
      // Every band is individually optional, so the bare object would also
      // accept `{}`. That is not the same statement as "no renditions": the
      // persisters branch on `if (event.imageUrls)`, and `{}` is truthy, so an
      // empty map would overwrite a populated `Event.imageUrls` with `{}`
      // instead of preserving it. `buildImageUrlVariants` already returns
      // `undefined` rather than `{}`; this keeps a hand-built lane from
      // reintroducing the blanking write at the boundary.
      .refine((value) => Object.values(value).some((url) => url !== undefined), {
        message: 'imageUrls must carry at least one rendition; omit it entirely when none exist',
      })
      .optional(),
    minPrice: z.number().nonnegative().optional(),
    maxPrice: z.number().nonnegative().optional(),
    // PIP-73 — honest-signal flag. `true` when the source explicitly surfaced a
    // price (paid range or confirmed free); `false`/`undefined` when the source
    // omitted price entirely. Persisters write the column as `false` when
    // omitted so the discover surface can distinguish "free" from "unknown".
    priceKnown: z.boolean().optional(),
    // API-1660 — tier cardinality + fee disclosure. Optional so Eventbrite
    // Apify (no upstream signal today) validates cleanly; positive integer
    // only for tier count (never invent from a min/max spread).
    priceTierCount: z.number().int().positive().optional(),
    plusFees: z.boolean().optional(),
    // PIP-161 — explicit free-entry / RSVP-only flags (decision D6). These
    // were on the TS interface but missing here, so Zod's default
    // key-stripping silently deleted them from every framework-validated
    // event before the persister ran (`validateStampedEvents` re-emits
    // `result.data`; caught by PIP-168/PIP-169 fixtures). `isFree` only on
    // an explicit free declaration; `rsvpOnly` only on an explicit RSVP
    // signal — never inferred (see the @loci/types field docs).
    isFree: z.boolean().optional(),
    rsvpOnly: z.boolean().optional(),
    ageRestriction: z.string().optional(),
    // FRE-1288 — approximate session length (minutes) from a trusted start/end
    // span. Positive integer only; omitted when unproven so Zod key-stripping
    // cannot silently delete a future transformer write (same class as PIP-168).
    runTimeMinutes: z.number().int().positive().optional(),
    tags: z.array(z.string().min(1)).optional(),
    sourceCategory: z.string().min(1).optional(),
    // PIP-234 — the source's own classification (segment / genre / subGenre),
    // a finer axis UNDERNEATH the six LOCI categories. Declared here because
    // this schema STRIPS undeclared keys: omitting them would silently delete
    // the transformer's write on the way to the persister — the same PIP-168
    // class of defect that PIP-234 itself exists to fix.
    //
    // `.min(1)` keeps a blank label out; omission (never an empty string) is
    // how a lane says "the source published nothing", which persists as NULL.
    // Only Ticketmaster fills all three; the Eventbrite lane fills the segment
    // only; lanes with no source taxonomy fill none. Never synthesized.
    sourceSegment: z.string().min(1).optional(),
    sourceGenre: z.string().min(1).optional(),
    sourceSubGenre: z.string().min(1).optional(),
    rawPayload: z.record(z.string(), z.unknown()).optional(),
  })
  .refine(
    (e) => e.endDatetime === undefined || new Date(e.startDatetime) <= new Date(e.endDatetime),
    { message: 'startDatetime must be ≤ endDatetime', path: ['endDatetime'] },
  )
  .refine((e) => e.minPrice === undefined || e.maxPrice === undefined || e.minPrice <= e.maxPrice, {
    message: 'minPrice must be ≤ maxPrice',
    path: ['maxPrice'],
  });

// Bidirectional type-check: both assignments must compile for the Zod schema
// and the TS interface to agree on structure. The single-direction cast used
// previously passed even when the interfaces diverged because `as` elides the
// object-literal structural check.
export type NormalizedEvent = z.infer<typeof normalizedEventSchema>;
const _fwd: NormalizedEventType = {} as NormalizedEvent;
const _rev: NormalizedEvent = {} as NormalizedEventType;
void _fwd;
void _rev;

// Key-exhaustiveness check (PIP-168): the assignability pair above is blind
// to MISSING OPTIONAL fields (absence of an optional property breaks
// assignability in neither direction) — which is exactly how `isFree`,
// `rsvpOnly`, and `googlePlaceId` were silently stripped by this schema for
// months. These resolve to `never` only when the schema and the interface
// declare the SAME key set, making the next omitted field a compile error.
type MissingFromSchema = Exclude<keyof NormalizedEventType, keyof NormalizedEvent>;
type ExtraInSchema = Exclude<keyof NormalizedEvent, keyof NormalizedEventType>;
const _keysExact: [MissingFromSchema, ExtraInSchema] extends [never, never] ? true : never = true;
void _keysExact;
